<template>
  <div class="account-page">
    <div class="account-shell">
      <header class="page-head">
        <div>
          <div class="eyebrow">Cuenta</div>
          <h1>Mi cuenta</h1>
          <p>Administra tu perfil, contraseña y suscripción.</p>
        </div>
        <div class="page-head-actions">
          <router-link class="back-btn" to="/">← Volver a notas</router-link>
          <button v-if="mode === 'user'" class="logout-btn" type="button" @click="handleLogout">Cerrar sesión</button>
        </div>
      </header>

      <div v-if="mode === 'guest' && !bootstrapping" class="guest-card">
        <h2>Inicia sesión para ver tu cuenta</h2>
        <p>Desde aquí podrás consultar tu plan, fechas de cobro y actualizar tus datos.</p>
        <button class="dark-btn" @click="authOpen = true">Entrar o crear cuenta</button>
      </div>

      <template v-else>
        <div v-if="bannerMessage" class="banner" :class="bannerType">{{ bannerMessage }}</div>

        <div class="grid">
          <section class="card">
            <div class="card-kicker">Perfil</div>
            <h2>Datos básicos</h2>
            <form class="form" @submit.prevent="saveProfile">
              <input v-model.trim="profile.nombre" type="text" placeholder="Nombre" required>
              <input v-model.trim="profile.username" type="text" placeholder="Usuario" required>
              <input v-model.trim="profile.email" type="email" placeholder="Correo" required>

              <div class="settings-block">
                <div>
                  <div class="settings-title">Notificaciones grupales</div>
                  <p class="settings-copy">Activa la campanita para enterarte cuando alguien agregue, actualice o marque tareas en tus hojas compartidas.</p>
                </div>

                <label class="toggle-row">
                  <input v-model="profile.notifications_enabled" class="toggle-input" type="checkbox">
                  <span class="toggle-pill" :class="{ active: profile.notifications_enabled }">
                    <span class="toggle-knob"></span>
                  </span>
                  <strong>{{ profile.notifications_enabled ? 'Activadas' : 'Desactivadas' }}</strong>
                </label>
              </div>

              <button class="dark-btn" :disabled="loading.profile">{{ loading.profile ? 'Guardando…' : 'Guardar cambios' }}</button>
            </form>
          </section>

          <section class="card">
            <div class="card-kicker">Seguridad</div>
            <h2>Contraseña</h2>
            <form class="form" @submit.prevent="savePassword">
              <input v-model="password.current_password" type="password" placeholder="Contraseña actual" required>
              <input v-model="password.new_password" type="password" placeholder="Nueva contraseña" required>
              <button class="dark-btn" :disabled="loading.password">{{ loading.password ? 'Actualizando…' : 'Actualizar contraseña' }}</button>
            </form>
          </section>

          <section class="card span-2">
            <div class="card-kicker">Suscripción</div>
            <div class="billing-head-row">
              <div>
                <h2>{{ billingPlanLabel }}</h2>
                <p>{{ billingStatusCopy }}</p>
              </div>
              <div class="billing-actions">
                <button v-if="!billing.ad_free_active" class="dark-btn" @click="plansOpen = true">Ver planes</button>
                <button v-else-if="canOpenPortal" class="soft-btn" @click="openPortal">Administrar</button>
              </div>
            </div>

            <div class="detail-grid">
              <div class="detail-item"><span>Estado</span><strong>{{ billing.status || 'free' }}</strong></div>
              <div class="detail-item"><span>Plan</span><strong>{{ billingPlanShortLabel }}</strong></div>
              <div class="detail-item"><span>Próximo corte</span><strong>{{ billing.current_period_end ? formatDate(billing.current_period_end) : (billing.ad_free_active ? 'Sin vencimiento' : '—') }}</strong></div>
              <div class="detail-item"><span>Límites actuales</span><strong>{{ limitsCopy }}</strong></div>
            </div>
          </section>

          <section class="card span-2">
            <div class="card-kicker">Pagos</div>
            <div class="history-head-row">
              <div>
                <h2>Historial</h2>
                <p class="history-copy">Consulta tus compras y abre un comprobante simple desde aquí.</p>
              </div>
              <button class="soft-btn small-btn" @click="loadHistory" :disabled="loading.history">{{ loading.history ? 'Actualizando…' : 'Actualizar historial' }}</button>
            </div>
            <div v-if="loading.history" class="empty">Cargando movimientos…</div>
            <div v-else-if="!history.length" class="empty">Todavía no hay pagos registrados.</div>
            <div v-else class="history-list">
              <button v-for="item in history" :key="item.id" type="button" class="history-item" @click="openReceipt(item)">
                <div>
                  <strong>{{ formatMoney(item.amount_paid || item.amount_due) }}</strong>
                  <span>{{ historyStatusLabel(item) }}</span>
                </div>
                <small>{{ historySummary(item) }}</small>
              </button>
            </div>
          </section>
        </div>
      </template>
    </div>

    <AuthModal :open="authOpen" :login-action="loginAction" :register-action="registerAction" @close="authOpen = false" />
    <PlanModal :open="plansOpen" :plans="billing.plans || []" @close="plansOpen = false" @select="startCheckout" />

    <div v-if="receiptOpen && receiptItem" class="receipt-overlay" @click.self="closeReceipt">
      <div class="receipt-modal">
        <div class="receipt-kicker">Comprobante</div>
        <h3>{{ receiptTitle(receiptItem) }}</h3>
        <p class="receipt-copy">{{ receiptCopy(receiptItem) }}</p>

        <div class="receipt-grid">
          <div class="receipt-row">
            <span>Monto</span>
            <strong>{{ formatMoney(receiptItem.amount_paid || receiptItem.amount_due) }}</strong>
          </div>
          <div class="receipt-row">
            <span>Estado</span>
            <strong>{{ historyStatusLabel(receiptItem) }}</strong>
          </div>
          <div class="receipt-row">
            <span>Fecha</span>
            <strong>{{ receiptItem.created_at ? formatDateTime(receiptItem.created_at) : '—' }}</strong>
          </div>
          <div class="receipt-row">
            <span>Moneda</span>
            <strong>{{ (receiptItem.currency || 'MXN').toUpperCase() }}</strong>
          </div>
          <div class="receipt-row full" v-if="receiptDescription(receiptItem)">
            <span>Detalle</span>
            <strong>{{ receiptDescription(receiptItem) }}</strong>
          </div>
        </div>

        <div class="receipt-actions">
          <a v-if="receiptItem.hosted_invoice_url" class="soft-btn link-btn" :href="receiptItem.hosted_invoice_url" target="_blank" rel="noreferrer">Abrir factura</a>
          <a v-if="receiptItem.invoice_pdf" class="soft-btn link-btn" :href="receiptItem.invoice_pdf" target="_blank" rel="noreferrer">Descargar PDF</a>
          <button class="dark-btn" type="button" @click="closeReceipt">Cerrar</button>
        </div>
      </div>
    </div>
    <div v-if="thanksOpen" class="modal-backdrop" @click.self="closeThanksModal">
    <div class="modal-card receipt-card thanks-card">
      <div class="receipt-eyebrow">Gracias por tu compra</div>
      <h3>Tu cuenta ya quedó ampliada</h3>
      <p class="receipt-copy">Ya ocultamos anuncios y tarjetas de upgrade dentro de la app. También se aplicaron los límites premium para tus grupos.</p>
      <div class="receipt-grid">
        <div><span>Plan</span><strong>{{ billingPlanShortLabel }}</strong></div>
        <div><span>Estado</span><strong>{{ billing.status || 'active' }}</strong></div>
      </div>
      <div class="receipt-actions">
        <button class="dark-btn" type="button" @click="closeThanksModal">Aceptar</button>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import api from '../services/api';
import AuthModal from '../components/AuthModal.vue';
import PlanModal from '../components/PlanModal.vue';

export default {
  name: 'AccountView',
  components: { AuthModal, PlanModal },
  data() {
    return {
      authOpen: false,
      plansOpen: false,
      bootstrapping: false,
      profile: { nombre: '', username: '', email: '', notifications_enabled: true },
      password: { current_password: '', new_password: '' },
      billing: { plans: [], limits: {} },
      history: [],
      loading: { profile: false, password: false, history: false },
      bannerMessage: '',
      bannerType: 'info',
      receiptOpen: false,
      receiptItem: null,
      thanksOpen: false,
    };
  },
  computed: {
    ...mapState(['mode', 'user']),
    billingPlanLabel() {
      const map = { monthly: 'Plan mensual', yearly: 'Plan anual', lifetime: 'Plan vitalicio' };
      return map[this.billing.plan] || 'Modo free';
    },
    billingPlanShortLabel() {
      const map = { monthly: 'Mensual', yearly: 'Anual', lifetime: 'Vitalicio' };
      return map[this.billing.plan] || 'Free';
    },
    billingStatusCopy() {
      if (this.billing.ad_free_active) {
        return 'Tu cuenta está ampliada. Ya no deberías ver anuncios ni tarjetas de upgrade dentro de la app.';
      }
      if ((this.billing.status === 'pending' || this.billing.status === 'processing') && !this.billing.ad_free_active) {
        return 'Estamos terminando de validar tu compra.';
      }
      return 'Aún estás en modo free.';
    },
    limitsCopy() {
      const limits = this.billing?.limits || {};
      const groups = limits.max_groups == null ? 'grupos ilimitados' : `${limits.max_groups} grupo`;
      const members = limits.max_members_per_group || 4;
      return `${groups}, ${members} usuarios/grupo`;
    },
    canOpenPortal() {
      return this.billing.ad_free_active && this.billing.plan !== 'lifetime';
    },
  },
  async mounted() {
    await this.ensureSessionReady();
    await this.handleBillingReturn();
    if (this.mode === 'user') {
      await this.loadAll();
    }
  },
  watch: {
    mode(value) {
      if (value === 'user') {
        this.authOpen = false;
        this.loadAll();
      }
    },
  },
  methods: {
    ...mapActions({
      loginAction: 'login',
      registerAction: 'register',
      startCheckoutAction: 'startCheckout',
      openBillingPortalAction: 'openBillingPortal',
      bootstrapAction: 'bootstrap',
      loadBillingStatusAction: 'loadBillingStatus',
      broadcastLocalRefreshAction: 'broadcastLocalRefresh',
      logoutAction: 'logout',
    }),
    ...mapMutations(['setBilling', 'setUser']),
    async ensureSessionReady() {
      if (this.mode === 'user') return;
      if (!localStorage.getItem('notas_access_token')) return;

      this.bootstrapping = true;
      try {
        await this.bootstrapAction();
      } catch (error) {
        console.warn(error);
      } finally {
        this.bootstrapping = false;
      }
    },
    applyBillingState(payload) {
      this.billing = { loaded: true, ...(payload || { plans: [], limits: {} }) };
      this.setBilling(this.billing);
    },
    async handleBillingReturn() {
      const params = new URLSearchParams(window.location.search);
      const status = params.get('billing');
      const plan = params.get('plan');
      const sessionId = params.get('session_id');
      const cleanAccountUrl = () => window.history.replaceState({}, '', '/cuenta');

      if (status === 'cancel') {
        this.bannerType = 'warn';
        this.bannerMessage = 'El flujo de pago fue cancelado.';
        cleanAccountUrl();
        return;
      }

      if (status !== 'success') {
        return;
      }

      if (this.mode !== 'user') {
        this.bannerType = 'warn';
        this.bannerMessage = 'Necesitamos una sesión iniciada para confirmar tu compra.';
        return;
      }

      this.bannerType = 'info';
      this.bannerMessage = 'Estamos confirmando tu pago con Stripe…';

      const attempts = [
        () => api.get('/billing/confirm-return', { params: { plan } }),
        () => sessionId ? api.get('/billing/verify-session', { params: { session_id: sessionId } }) : null,
        () => api.get('/billing/status'),
      ];

      let resolved = false;
      let lastError = null;

      for (let round = 0; round < 6 && !resolved; round += 1) {
        for (const run of attempts) {
          if (typeof run !== 'function') continue;
          try {
            const request = run();
            if (!request) continue;
            const response = await request;
            const data = response.data?.data || null;
            if (data) {
              this.applyBillingState(data);
            }
            const currentPlan = String(this.billing?.plan || '').toLowerCase();
            const currentStatus = String(this.billing?.status || '').toLowerCase();
            if (
              this.billing?.ad_free_active
              || ['active', 'trialing', 'complete', 'paid'].includes(currentStatus)
              || (['monthly', 'yearly', 'lifetime'].includes(currentPlan) && currentStatus !== 'pending')
            ) {
              this.bannerType = 'success';
              this.bannerMessage = 'Pago validado correctamente. Tu cuenta ya quedó sin anuncios.';
              this.thanksOpen = true;
              resolved = true;
              break;
            }
          } catch (error) {
            lastError = error;
          }
        }

        if (!resolved && round < 5) {
          await new Promise((resolve) => setTimeout(resolve, 1800));
        }
      }

      if (!resolved) {
        this.bannerType = 'warn';
        this.bannerMessage = lastError?.response?.data?.message || 'No se pudo validar el pago todavía. Puedes recargar o volver a intentarlo en unos segundos.';
      } else {
        await this.loadBillingStatusAction();
        await this.broadcastLocalRefreshAction();
        window.dispatchEvent(new CustomEvent('billing-updated', { detail: this.billing }));
      }

      cleanAccountUrl();
    },
    async loadAll() {
      try {
        const [profileResponse, billingResponse] = await Promise.all([
          api.get('/account/profile'),
          api.get('/billing/status'),
        ]);
        const data = profileResponse.data?.data || {};
        this.profile = {
          nombre: data.nombre || '',
          username: data.username || '',
          email: data.email || '',
          notifications_enabled: Number(data.notifications_enabled ?? 1) === 1,
        };
        this.setUser({ ...(this.user || {}), ...data, notifications_enabled: Number(data.notifications_enabled ?? 1) });
        this.applyBillingState(billingResponse.data?.data || { plans: [], limits: {} });
      } catch (error) {
        this.bannerType = 'warn';
        this.bannerMessage = error?.response?.data?.message || 'No se pudieron cargar los datos de la cuenta.';
      }

      await this.loadHistory();
    },
    async loadHistory() {
      this.loading.history = true;
      try {
        const response = await api.get('/billing/history');
        this.history = response.data?.data || [];
      } catch (error) {
        this.history = [];
      } finally {
        this.loading.history = false;
      }
    },
    async saveProfile() {
      this.loading.profile = true;
      try {
        const payload = {
          ...this.profile,
          notifications_enabled: this.profile.notifications_enabled ? 1 : 0,
        };
        const response = await api.patch('/account/profile', payload);
        const data = response.data?.data || payload;
        this.profile = {
          nombre: data.nombre || '',
          username: data.username || '',
          email: data.email || '',
          notifications_enabled: Number(data.notifications_enabled ?? 1) === 1,
        };
        this.setUser({ ...(this.user || {}), ...data, notifications_enabled: Number(data.notifications_enabled ?? 1) });
        this.bannerType = 'success';
        this.bannerMessage = 'Perfil actualizado.';
      } catch (error) {
        this.bannerType = 'warn';
        this.bannerMessage = error?.response?.data?.message || 'No se pudo guardar el perfil.';
      } finally {
        this.loading.profile = false;
      }
    },
    async savePassword() {
      this.loading.password = true;
      try {
        await api.post('/account/password', this.password);
        this.password = { current_password: '', new_password: '' };
        this.bannerType = 'success';
        this.bannerMessage = 'Contraseña actualizada.';
      } catch (error) {
        this.bannerType = 'warn';
        this.bannerMessage = error?.response?.data?.message || 'No se pudo actualizar la contraseña.';
      } finally {
        this.loading.password = false;
      }
    },
    async handleLogout() {
      await this.logoutAction();
      this.$router.replace('/');
    },
    async startCheckout(plan) {
      this.plansOpen = false;
      await this.startCheckoutAction(plan);
    },
    async openPortal() {
      try {
        await this.openBillingPortalAction();
      } catch (error) {
        this.bannerType = 'warn';
        this.bannerMessage = error?.response?.data?.message || 'No se pudo abrir el portal.';
      }
    },
    openReceipt(item) {
      this.receiptItem = item;
      this.receiptOpen = true;
    },
    closeReceipt() {
      this.receiptOpen = false;
      this.receiptItem = null;
    },
    closeThanksModal() {
      this.thanksOpen = false;
    },
    historyStatusLabel(item) {
      const value = String(item?.status || 'pending').toLowerCase();
      const labels = {
        paid: 'Pagado',
        succeeded: 'Pagado',
        complete: 'Completado',
        active: 'Activo',
        open: 'Pendiente',
        pending: 'Pendiente',
        processing: 'Procesando',
        canceled: 'Cancelado',
        incomplete: 'Incompleto',
      };
      return labels[value] || item?.status || 'Pendiente';
    },
    historySummary(item) {
      const parts = [this.receiptTitle(item)];
      if (item?.created_at) {
        parts.push(this.formatDate(item.created_at));
      }
      return parts.filter(Boolean).join(' · ');
    },
    receiptTitle(item) {
      if (item?.plan_label) return item.plan_label;
      if (item?.description) return item.description;
      if (item?.type === 'invoice') return 'Pago de suscripción';
      if (item?.type === 'payment_intent') return 'Pago único';
      return 'Movimiento de pago';
    },
    receiptCopy(item) {
      if (item?.status === 'paid' || item?.status === 'succeeded' || item?.status === 'active') {
        return 'Tu compra fue registrada correctamente. Aquí tienes un resumen fácil de consultar.';
      }
      return 'Este es el resumen del movimiento asociado a tu cuenta.';
    },
    receiptDescription(item) {
      return item?.description || item?.summary || '';
    },
    formatDate(value) {
      return new Date(value.replace(' ', 'T') + 'Z').toLocaleDateString('es-MX', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    },
    formatDateTime(value) {
      return new Date(value.replace(' ', 'T') + 'Z').toLocaleString('es-MX', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
  },
};
</script>

<style scoped>
.account-page {
  min-height: 100vh;
  padding: 28px;
  background: linear-gradient(180deg, #f6f7fb 0%, #eef1f7 100%);
}
.account-shell {
  width: min(1100px, 100%);
  margin: 0 auto;
}
.page-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}
.page-head-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}
.eyebrow,
.card-kicker,
.receipt-kicker {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .12em;
  color: #64748b;
}
h1, h2, h3 { color: #0f172a; }
h1 { margin: 8px 0 0; font-size: 36px; }
.page-head p, .history-copy, .receipt-copy { margin: 8px 0 0; color: #475569; }
.back-btn, .logout-btn, .dark-btn, .soft-btn, .link-btn {
  border: none;
  border-radius: 16px;
  padding: 12px 16px;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
}
.back-btn, .soft-btn, .link-btn { background: white; color: #0f172a; border: 1px solid rgba(148, 163, 184, 0.25); }
.logout-btn, .dark-btn { background: #111827; color: white; }
.small-btn { width: auto; }
.guest-card,
.card,
.receipt-modal {
  background: rgba(255,255,255,0.92);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 24px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
}
.guest-card { margin-top: 24px; padding: 28px; display: grid; gap: 12px; }
.banner {
  margin-top: 22px;
  padding: 14px 16px;
  border-radius: 16px;
  font-weight: 700;
}
.banner.info { background: rgba(59,130,246,.12); color: #1d4ed8; }
.banner.success { background: rgba(34,197,94,.14); color: #166534; }
.banner.warn { background: rgba(245,158,11,.16); color: #92400e; }
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 24px;
}
.card { padding: 24px; }
.span-2 { grid-column: span 2; }
.form { display: grid; gap: 12px; margin-top: 12px; }
.settings-block {
  margin-top: 6px;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: #f8fafc;
  padding: 16px;
  display: grid;
  gap: 14px;
}
.settings-title {
  font-weight: 800;
  color: #0f172a;
}
.settings-copy {
  margin: 6px 0 0;
  color: #64748b;
}
.toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  width: fit-content;
}
.toggle-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.toggle-pill {
  width: 54px;
  height: 30px;
  border-radius: 999px;
  background: #cbd5e1;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  transition: background .22s ease;
}
.toggle-pill.active {
  background: #2563eb;
}
.toggle-knob {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
  transform: translateX(0);
  transition: transform .22s ease;
}
.toggle-pill.active .toggle-knob {
  transform: translateX(24px);
}
input {
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #fff;
  padding: 14px 16px;
}
.billing-head-row,
.history-head-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.billing-actions { display: flex; gap: 10px; }
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}
.detail-item {
  border-radius: 18px;
  padding: 14px;
  background: #f8fafc;
  display: grid;
  gap: 6px;
}
.detail-item span { color: #64748b; font-size: 13px; }
.history-list { display: grid; gap: 10px; margin-top: 14px; }
.history-item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  background: #fff;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  gap: 14px;
  cursor: pointer;
}
.history-item div { display: grid; gap: 4px; }
.history-item span, .history-item small, .empty { color: #64748b; }
.receipt-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 100;
}
.receipt-modal {
  width: min(620px, 100%);
  padding: 24px;
}
.receipt-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.receipt-row {
  background: #f8fafc;
  border-radius: 18px;
  padding: 14px;
  display: grid;
  gap: 6px;
}
.receipt-row span { color: #64748b; font-size: 13px; }
.receipt-row.full { grid-column: span 2; }
.receipt-actions {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}
@media (max-width: 860px) {
  .account-page { padding: 18px; }
  .page-head, .billing-head-row, .history-head-row { flex-direction: column; }
  .grid, .detail-grid, .receipt-grid { grid-template-columns: 1fr; }
  .span-2, .receipt-row.full { grid-column: span 1; }
}
</style>
